/**
 * Is Live at this Ableton Control step's value? (DEC-291)
 *
 * One answer, read by both surfaces that light from it: a full row's cells
 * (`AbletonRow.tsx`) and a one-step Ableton Control button (`FireButton.tsx`).
 * Before this, only a metronome button lit, so a "1/16" button or a "Play"
 * button sat dark beside a row lit from the same readback, and looked broken.
 *
 * The light is ALWAYS the readback, never what we last sent (DEC-124's rule,
 * as the Rec Quantize row already follows it): Live can be changed from its
 * own window, from OPSetlist or from OPBridge's dashboard, and an optimistic
 * light lies the moment any of them does.
 *
 *   true   Live reports this value.
 *   false  Live reports a different value.
 *   null   we do not know - nothing heard, the clock is not Live's, or the
 *          step has no value of its own (Continue, Resync, Sustain kill).
 *          Drawn exactly like false: a light claims only what it knows.
 *
 * WHERE EACH VALUE COMES FROM
 *   tempo / time signature / play-stop   `liveClockReport()` - only when it
 *       is fresh AND its source is Live's (`ableton*`, the timeline editor's
 *       rule). A practice-clock step reads the practice clock instead, which
 *       is local and always known.
 *   record / launch quantize / metronome   `abletonState()`, the device's
 *       own report. Record quantize on OP-Quantize reads the devices' report,
 *       exactly as the Rec Quantize row does.
 *
 * ⚠️ The two quantize vocabularies stay apart here too (DEC-181): each step
 * type is parsed by its own module's `normalize*`, never one for both.
 */

import { normalizeRecQuant, normalizeLaunchQuant, type AbletonStep } from '@opbuttons/schema';
import { abletonState, liveClockReport, type LiveClockReport } from '../opbridge/clockPoll.ts';
import { practiceBpm, practiceIsPlaying, practiceSigDen, practiceSigNum } from '../clock/practiceClock.ts';

export type StepLit = boolean | null;

/** The clock report when it describes Live right now, else null. */
function liveClock(): LiveClockReport | null {
  const r = liveClockReport();
  return r && r.fresh && r.source.startsWith('ableton') ? r : null;
}

/** Live reports tempo as a float; a set of 120 reads back 120.0, give or take
 *  float noise. The face shows at most 2 decimals, so match at that. */
function sameBpm(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.005;
}

/** See the header. */
export function stepLit(step: AbletonStep): StepLit {
  switch (step.type) {
    case 'ableton.tempo': {
      if (step.clock === 'practice') return sameBpm(practiceBpm(), step.bpm);
      const c = liveClock();
      return c ? sameBpm(c.tempo, step.bpm) : null;
    }
    case 'ableton.timesig': {
      if (step.clock === 'practice') {
        return practiceSigNum() === step.numerator && practiceSigDen() === step.denominator;
      }
      const c = liveClock();
      // 0 is "never received" upstream, not a signature.
      if (!c || c.sigNum <= 0 || c.sigDen <= 0) return null;
      return c.sigNum === step.numerator && c.sigDen === step.denominator;
    }
    case 'ableton.transport': {
      // Continue is a verb with no state of its own: once it lands, Live is
      // simply playing, which Play already shows. Lighting both would read
      // as two different things being true.
      if (step.action === 'continue') return null;
      const c = liveClock();
      if (!c) return null;
      return step.action === 'play' ? c.isPlaying : !c.isPlaying;
    }
    case 'practice.transport':
      return step.action === 'play' ? practiceIsPlaying() : !practiceIsPlaying();
    case 'ableton.recquant': {
      const want = normalizeRecQuant(step.label);
      if (want === null) return null;
      if (step.via === 'op-quantize') {
        const q = abletonState().opQuantize;
        // A null grid is the instances disagreeing ("mixed"): nothing is lit,
        // as on the row.
        return q ? normalizeRecQuant(q.grid) === want : null;
      }
      const live = normalizeRecQuant(abletonState().recQuant?.label);
      return live === null ? null : live === want;
    }
    case 'ableton.launchquant': {
      const want = normalizeLaunchQuant(step.label);
      if (want === null) return null;
      const live = normalizeLaunchQuant(abletonState().launchQuant?.label);
      return live === null ? null : live === want;
    }
    case 'ableton.metronome': {
      const m = abletonState().metronome;
      if (!m) return null;
      return step.mode === 'off' ? !m.on : m.on;
    }
    case 'ableton.resync':
    case 'ableton.sustainkill':
    case 'ableton.endsessionrecord':
    case 'opmotion.crossfade':
    case 'opmotion.capture':
    case 'opmotion.land':
      return null;
  }
}

/** Whether a send of this step has LANDED, for a row's pending state. The
 *  same as {@link stepLit} except Continue, which has landed once Live is
 *  playing even though it never lights. */
export function stepLanded(step: AbletonStep): StepLit {
  if (step.type === 'ableton.transport' && step.action === 'continue') {
    const c = liveClock();
    return c ? c.isPlaying : null;
  }
  return stepLit(step);
}

/** Whether a button carrying only this step can light at all. Resync and
 *  Sustain kill are one-shots with nothing to report; Continue is a verb. */
export function stepCanLight(step: AbletonStep): boolean {
  if (step.type === 'ableton.resync' || step.type === 'ableton.sustainkill' || step.type === 'ableton.endsessionrecord' || step.type === 'opmotion.crossfade' || step.type === 'opmotion.capture' || step.type === 'opmotion.land') return false;
  if (step.type === 'ableton.transport' && step.action === 'continue') return false;
  return true;
}
